import { createContext, useContext, useState, useEffect } from "react";
import PropTypes from "prop-types";
import sessionService from "services/sessionService";

import { API_ENDPOINTS } from "config/api";

// Create AuthContext
const AuthContext = createContext();

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// AuthProvider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [monitoringRecordId, setMonitoringRecordId] = useState(null); // Store monitoring record ID

  // Logout function with proper cleanup
  const logout = () => {
    // Call backend to log logout time
    if (user && user.username) {
      // Fire and forget - don't await to ensure UI logout is instant
      fetch(API_ENDPOINTS.LOGOUT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: user.username,
          monitoringRecordId: monitoringRecordId // Send the exact record ID
        })
      }).catch(err => console.error('Logout logging error:', err));
    }

    // End session monitoring
    sessionService.endSession();

    // Clear all authentication data
    localStorage.removeItem("authToken");
    localStorage.removeItem("userData");
    localStorage.removeItem("user"); // Legacy cleanup
    sessionStorage.removeItem("monitoringRecordId"); // Clear monitoring record ID
    sessionStorage.clear(); // Clear session data

    // Clear browser history to prevent back button issues
    window.history.replaceState(null, "", "/authentication/sign-in");

    // Update state
    setUser(null);
    setIsAuthenticated(false);
    setMonitoringRecordId(null); // Clear from state

    // User logged out successfully - logging removed for silent mode
  };

  // Check for existing auth on mount and verify with backend
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    const userData = localStorage.getItem("userData");

    if (token && userData) {
      try {
        const parsedUser = JSON.parse(userData);

        // Check if session is still valid locally
        if (sessionService.isSessionValid()) {
          setUser(parsedUser);
          setIsAuthenticated(true);
          // Restore monitoring record ID from SESSION STORAGE (tab specific)
          const storedRecordId = sessionStorage.getItem("monitoringRecordId");
          if (storedRecordId) {
            setMonitoringRecordId(storedRecordId);
          }
          // Resume session monitoring
          sessionService.startSession(logout);

          // Verify with backend that the user still exists and hasn't been deleted
          fetch(API_ENDPOINTS.VERIFY_TOKEN, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ token }),
          })
            .then((res) => {
              if (!res.ok) {
                console.warn('🔒 User account is no longer valid or was deleted. Logging out...');
                logout();
              }
            })
            .catch((err) => {
              // Network temporary glitch, do not log out on pure network error
            });
        } else {
          // Session expired, clean up
          localStorage.removeItem("authToken");
          localStorage.removeItem("userData");
          sessionStorage.removeItem("monitoringRecordId");
          sessionService.endSession();
        }
      } catch (error) {
        localStorage.removeItem("authToken");
        localStorage.removeItem("userData");
        sessionStorage.removeItem("monitoringRecordId");
        sessionService.endSession();
      }
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 📡 Real-time SSE listener: instantly log out if this user account is deleted by admin
  useEffect(() => {
    if (!isAuthenticated || !user?.username) return;

    const currentUsername = String(user.username).toLowerCase().trim();
    let eventSource = null;

    try {
      const token = localStorage.getItem("token") || localStorage.getItem("authToken");
      eventSource = new EventSource(`/api/fdo/stream?token=${token || ''}`);

      eventSource.onmessage = (event) => {
        try {
          if (!event.data) return;
          const data = JSON.parse(event.data);
          if (data && (data.type === 'user_deleted' || data.type === 'account_deleted')) {
            const targetUsername = String(data.username || '').toLowerCase().trim();
            if (targetUsername && targetUsername === currentUsername) {
              console.warn(`🔒 Account "${user.username}" was deleted by administrator. Auto logging out now...`);
              logout();
            }
          }
        } catch (e) {
          // Ignore JSON parse errors for non-JSON events
        }
      };

      eventSource.onerror = () => {
        // EventSource will auto-reconnect
      };
    } catch (err) {
      console.warn('SSE auth listener notice:', err);
    }

    return () => {
      if (eventSource) {
        eventSource.close();
      }
    };
  }, [isAuthenticated, user?.username]);

  // 🔄 Periodic & tab-focus verification: check if account was deleted while away
  useEffect(() => {
    if (!isAuthenticated || !user?.username) return;

    const verifyActiveSession = async () => {
      const token = localStorage.getItem("authToken");
      if (!token) {
        logout();
        return;
      }

      try {
        const res = await fetch(API_ENDPOINTS.VERIFY_TOKEN, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token }),
        });

        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          console.warn('🔒 User session invalid or account was deleted by admin:', data?.message);
          logout();
        }
      } catch (e) {
        // Network temporary glitch, do not log out on pure network error
      }
    };

    // Check periodically every 20 seconds
    const intervalId = setInterval(verifyActiveSession, 20000);

    // Check when user returns to this tab / window
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        verifyActiveSession();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', verifyActiveSession);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', verifyActiveSession);
    };
  }, [isAuthenticated, user?.username]);

  // Handle browser close/tab close (beforeunload event)
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      // Only log if user is authenticated
      if (user && user.username) {
        // Use sendBeacon for reliability - it works even when page is unloading
        const data = JSON.stringify({
          username: user.username,
          monitoringRecordId: monitoringRecordId // Send the exact record ID
        });
        const blob = new Blob([data], { type: 'application/json' });

        // sendBeacon is specifically designed for this use case
        navigator.sendBeacon(API_ENDPOINTS.BROWSER_CLOSE, blob);
      }
    };

    // Add event listener
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup on unmount
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]); // Re-run if user changes

  // Login function
  const login = (token, userData, recordId = null) => {
    localStorage.setItem("authToken", token);
    localStorage.setItem("userData", JSON.stringify(userData));
    localStorage.setItem("userRole", userData.role || "user"); // Save role separately for easy access

    // Store monitoring record ID
    if (recordId) {
      sessionStorage.setItem("monitoringRecordId", recordId);
      setMonitoringRecordId(recordId);
    }

    // Store user permissions in localStorage for custom role
    if (userData && userData.username && userData.permissions) {
      const permsStr = typeof userData.permissions === 'string'
        ? userData.permissions
        : JSON.stringify(userData.permissions);
      localStorage.setItem(`permissions_${userData.username}`, permsStr);
    }

    setUser(userData);
    setIsAuthenticated(true);

    // Start session timeout monitoring
    sessionService.startSession(logout);
  };

  // Check if user is admin or has view_only access
  const isAdmin = () => {
    return user?.role === "admin" || user?.role === "view_only";
  };

  // Check if user is regular user
  const isUser = () => {
    return user?.role === "user";
  };

  // Check if user has view-only access
  const isViewOnly = () => {
    return user?.role === "view_only";
  };


  // Check if user has custom permissions
  const isCustom = () => {
    return user?.role === "custom";
  };

  // Check if user is full admin (not view-only)
  const isFullAdmin = () => {
    return user?.role === "admin";
  };

  // Check specific permissions for custom users
  const hasPermission = (page, level = 'view') => {
    if (user?.role !== 'custom') return false;

    // First try to get permissions from user object
    if (user?.permissions) {
      try {
        const permissions = typeof user.permissions === 'string'
          ? JSON.parse(user.permissions)
          : user.permissions;

        return permissions?.[page]?.[level] === true;
      } catch (error) {
        if (user.permissions === "123") {
          const defaultPermissions = {
            fdoPanel: { view: true, complete: false, hideContact: false, hideDownload: false, view1Week: false },
            cleaning: { view: true, complete: false },
            upsells: { view: true, complete: false },
            revenue: { view: true, complete: false },
            payment: { view: true, complete: false },
            qbPayment: { view: true, complete: false },
            resetPassword: true
          };
          return defaultPermissions?.[page]?.[level] === true;
        }
      }
    }

    // Fallback: get permissions from localStorage
    try {
      const storedPermissions = localStorage.getItem(`permissions_${user.username}`);
      if (storedPermissions) {
        const permissions = JSON.parse(storedPermissions);
        return permissions?.[page]?.[level] === true;
      }
    } catch (error) {
      // Error getting permissions from localStorage - logging removed for silent mode
    }

    return false;
  };

  // Helper to get allowed date range based on permissions
  const getAllowedDateRange = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Start of day PKT equivalent approximation
    
    // Admin: 30 days max range
    if (user?.role === "admin") {
      return { rangeLimit: 30, canChangeDate: true };
    }
    
    // Custom: Check view1Week permission
    if (user?.role === "custom" && hasPermission("fdoPanel", "view1Week")) {
      return { rangeLimit: 6, canChangeDate: true };
    }
    
    // Standard User or Custom without permission: Only today
    return { rangeLimit: 0, canChangeDate: false };
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        loading,
        login,
        logout,
        isAdmin,
        isUser,
        isViewOnly,
        isCustom,
        isFullAdmin,
        hasPermission,
        getAllowedDateRange,
        monitoringRecordId,
        setMonitoringRecordId
      }}
    >{children}</AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AuthContext;
